import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  TemplateRef
} from '@angular/core';

export interface DataTableColumn {
  name: string;                     // Column header label
  prop: string;                     // Property name in the data objects
  sortable?: boolean;               // Whether the column is sortable (default: true)
  width?: number;                   // Column width in pixels
  cellTemplate?: TemplateRef<any>;  // (Optional) Custom cell template
}

export interface DataTableConfig {
  title?: string;             // (Optional) Title to show in the table header
  columns: DataTableColumn[]; // Array of column definitions
  data: any[];                // The complete array of row objects
  headerHeight?: number;      // (Optional) Header row height (default: 50)
  rowHeight?: number | 'auto';// (Optional) Row height (default: 50)
  limit?: number;             // (Optional) Number of rows per page (default: 10)
}

@Component({
    selector: 'smt-table',
    templateUrl: './smartdine-table.component.html',
    styleUrls: ['./smartdine-table.component.scss'],
    standalone: false
})
export class SmartdineTableComponent implements OnInit, OnChanges {
  @Input() config: DataTableConfig;

  public rows: any[] = [];
  public allRows: any[] = [];
  public columns: any[] = [];
  
  public headerHeight = 50;
  public rowHeight: number | 'auto' = 50;
  public limit = 10;

  public tableHeight: number = 300;

  public currentPage: number = 1;
  public totalPages: number = 1;
  public pageSizes: number[] = [5, 10, 25, 50];

  public navBarHeight: number = 56;
  public filterHeight: number = 50; 
  public spacing: number = 20;

  ngOnInit() {
    this.calculateTableHeight();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config && this.config) {
      // Set up the column definitions.
      this.columns = this.config.columns.map(col => ({
        name: col.name,
        prop: col.prop,
        sortable: col.sortable !== undefined ? col.sortable : true,
        width: col.width,
        cellTemplate: col.cellTemplate
      }));

      // Allow the config to override the default table settings.
      if (this.config.headerHeight) {
        this.headerHeight = this.config.headerHeight;
      }
      if (this.config.rowHeight) {
        this.rowHeight = this.config.rowHeight;
      }
      if (this.config.limit) {
        this.limit = this.config.limit;
      }

      // Save all rows and set up pagination.
      this.allRows = this.config.data || [];
      this.currentPage = 1;
      this.totalPages = Math.ceil(this.allRows.length / this.limit) || 1;
      this.updateDisplayRows();
    }
  }

  /** Updates the visible rows based on the current page and page size */
  updateDisplayRows() {
    const start = (this.currentPage - 1) * this.limit;
    const end = start + this.limit;
    this.rows = this.allRows.slice(start, end);
  }

  /** Called when the page size (limit) is changed via the ion-select control */
  onPageSizeChange(event: any) {
    this.limit = +event.detail.value; // Ensure the value is numeric
    this.currentPage = 1;
    this.totalPages = Math.ceil(this.allRows.length / this.limit) || 1;
    this.updateDisplayRows();
  }

  /** Navigate to the previous page (if possible) */
  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateDisplayRows();
    }
  }

  /** Navigate to the next page (if possible) */
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updateDisplayRows();
    }
  }

  /** Listen to window resize events to recalculate the table’s container height */
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.calculateTableHeight();
  }

  calculateTableHeight() {
    this.tableHeight = window.innerHeight - this.navBarHeight - this.filterHeight - this.spacing;
  }
}
